import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Skeleton,
  Chip,
} from '@mui/material'
import PaymentsIcon from '@mui/icons-material/Payments'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { DashboardStatsDTO } from '@school-homestead/shared'

function StatCard({
  title,
  value,
  subtitle,
  icon,
  color = 'primary.main',
}: {
  title: string
  value: string
  subtitle: string
  icon: React.ReactNode
  color?: string
}) {
  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="overline" color="text.secondary" fontWeight={600}>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={700} color={color} sx={{ my: 0.5 }}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
          <Box
            sx={{
              bgcolor: `${color}18`,
              borderRadius: 2,
              p: 1.25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

function StatCardSkeleton() {
  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Skeleton variant="text" width={100} height={20} />
        <Skeleton variant="text" width={140} height={48} sx={{ my: 0.5 }} />
        <Skeleton variant="text" width={80} height={20} />
      </CardContent>
    </Card>
  )
}

export default function DashboardPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const res = await api.get<DashboardStatsDTO>('/api/dashboard/stats')
      if (!res.success) throw new Error(res.error)
      return res.data
    },
  })

  const totalMonthlyExpenses = data
    ? Object.values(data.monthlyExpenses).reduce((sum, v) => sum + v, 0)
    : 0

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3}>
        Dashboard
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load dashboard data. Please try again.
        </Alert>
      )}

      {/* Stat cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={4}>
          {isLoading ? (
            <StatCardSkeleton />
          ) : (
            <StatCard
              title="YTD Payroll"
              value={`$${(data?.ytdPayroll ?? 0).toLocaleString()}`}
              subtitle="paid this year"
              icon={<PaymentsIcon fontSize="medium" />}
              color="secondary.main"
            />
          )}
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          {isLoading ? (
            <StatCardSkeleton />
          ) : (
            <StatCard
              title="Monthly Expenses"
              value={`$${totalMonthlyExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              subtitle="spent this month"
              icon={<TrendingUpIcon fontSize="medium" />}
              color="error.main"
            />
          )}
        </Grid>
      </Grid>

      {/* Monthly expenses breakdown */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Expenses This Month — by Category
          </Typography>

          {isLoading ? (
            <Box>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rectangular" height={44} sx={{ mb: 1, borderRadius: 1 }} />
              ))}
            </Box>
          ) : data && Object.keys(data.monthlyExpenses).length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Amount
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Share
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(data.monthlyExpenses)
                    .sort(([, a], [, b]) => b - a)
                    .map(([cat, amt]) => (
                      <TableRow key={cat} hover>
                        <TableCell>
                          <Chip label={cat} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">
                          ${amt.toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">
                            {totalMonthlyExpenses > 0
                              ? `${((amt / totalMonthlyExpenses) * 100).toFixed(1)}%`
                              : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      ${totalMonthlyExpenses.toFixed(2)}
                    </TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
              No expenses recorded this month.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}
